import { useLog } from "@/composables/log";
import useToast from "@/composables/toast";
import { Character } from "@/types/character";
import { router } from "@inertiajs/vue3";
import {
    computed,
    MaybeRef,
    nextTick,
    onMounted,
    ref,
    toValue,
    watch,
    watchEffect,
} from "vue";
import { route } from "ziggy-js";

interface ReloadData {
    slot: number;
    characters: string;
    rotations: string;
    box?: number;
    categories?: string;
    category_affinity?: number;
    order?: string;
    search?: string;
}

interface ReloadOptions {
    data: ReloadData;
    only?: string[];
}

/**
 * Composable for the team builder page.
 */
export function useTeamBuilder() {
    const log = useLog();

    //==========================================================================
    // State
    //==========================================================================

    const characters = ref<Array<Character | null>>([]);

    watchEffect(() => {
        characters.value = [
            ...characters.value,
            ...Array(7 - characters.value.length).fill(null),
        ];
    });

    const slot = ref<number | null>(null);
    const nextSlot = computed(() => characters.value.indexOf(null));
    const rotations = ref<Array<number | null>>([]);

    watchEffect(() => {
        rotations.value = [
            ...rotations.value,
            ...new Array(characters.value.length - rotations.value.length).fill(
                null,
            ),
        ];
    });

    const boxUser = ref<number | null>(null);
    const categories = ref<number[]>([]);
    const categoriesPartial = ref<boolean>(false);
    const categoryAffinity = ref<number | null>(null);
    const order = ref<string | null>(null);
    const search = ref<string | null>(null);

    const reloadOptions = computed<ReloadOptions>((): ReloadOptions => {
        return {
            data: {
                box: boxUser.value,
                categories:
                    categories.value.join(",") +
                    (categories.value.length > 0 && categoriesPartial.value
                        ? ";1"
                        : ""),
                category_affinity: categoryAffinity.value,
                order: order.value,
                search: search.value,
                characters: characters.value
                    .map((c: Character) => (c ? c.id : null))
                    .join(","),
                rotations: rotations.value.join(","),
                slot: slot.value ?? 0,
            },
        };
    });

    const previousOrder = ref<string | null>(null);
    const loading = ref(false);

    //==========================================================================
    // Methods
    //==========================================================================

    /**
     * Sorts the query string.
     *
     * @param isPrefetch Whether the query string is for prefetching.
     *
     * @returns Sorted query string.
     */
    const sortedData = (isPrefetch: boolean): object => {
        const data = {
            ...reloadOptions.value.data,
            ...(isPrefetch && {
                slot: nextSlot.value === -1 ? 0 : nextSlot.value,
            }),
        };

        log.debug("Sorting data...", data);

        return Object.fromEntries(
            Object.entries(data)
                .sort()
                .filter(
                    ([, v]) =>
                        v !== null &&
                        v !== "" &&
                        v !==
                            new Array(characters.value.length)
                                .fill(null)
                                .join(","),
                ),
        );
    };

    /**
     * Reloads the available characters, based on the {@link reloadOptions}.
     *
     * @param onSuccess Callback to be executed on success.
     * @param onError Callback to be executed on error.
     */
    const reloadAvailableCharacters = (
        onSuccess = () => {},
        onError = () => {},
    ): void => {
        if (isRotationSlot()) {
            previousOrder.value = order.value;
            order.value = "links";
        } else if (previousOrder.value) {
            order.value = previousOrder.value;
            previousOrder.value = null;
        }

        router.visit(route("teams.create"), {
            data: { ...sortedData(false) },
            onBefore: () => {
                loading.value = true;

                log.debug(
                    "Reloading available characters...",
                    reloadOptions.value.data,
                );
            },
            onSuccess: () => {
                log.info("Available characters reloaded.");

                onSuccess();
            },
            onError: (errors: object) => {
                log.error(
                    "An error occurred while reloading available characters.",
                    {
                        errors: errors,
                    },
                );

                useToast({
                    title: "Oops!",
                    text: "An error occurred. Pleas try again later.",
                    icon: "error",
                });

                onError();
            },
            onFinish: () => {
                loading.value = false;
            },
            preserveScroll: true,
            preserveState: true,
        });
    };

    /**
     * Selects a slot or deselects it if it's already selected.
     *
     * @param selectedSlot
     * @param onSelected Callback to be executed on slot selection.
     */
    const selectSlot = (
        selectedSlot: MaybeRef<number>,
        onSelected = () => {},
    ): void => {
        const slotValue = toValue(selectedSlot);

        if (slot.value === slotValue) {
            slot.value = null;

            log.debug("Slot deselected", {
                slot: slotValue,
            });

            return;
        }

        log.debug("Slot selected", {
            old: slot.value,
            new: slotValue,
        });

        slot.value = slotValue;

        reloadAvailableCharacters(
            () => {
                onSelected();
            },
            () => {
                slot.value = null;
            },
        );
    };

    /**
     * Adds a character to the selected characters.
     *
     * @param character Character to be added.
     */
    const addCharacter = (character: MaybeRef<Character>): void => {
        const characterValue = toValue(character);

        if (slot.value === null) {
            return;
        }

        log.debug("Adding character to team...", {
            slot: slot.value,
            character: characterValue,
            selected_characters: characters.value,
        });

        characters.value[slot.value] = characterValue;

        if (slot.value === 0) {
            characters.value[6] = characterValue;
        }

        router.reload({
            data: { ...sortedData(false) },
            only: ["selected_characters"],
        });

        slot.value = null;
    };

    /**
     * Filters the available characters.
     */
    const filter = (): void => {
        if (slot.value === null) {
            return;
        }

        log.debug("Filtering characters...");

        reloadAvailableCharacters(() =>
            log.info("Characters filtered.", reloadOptions.value.data),
        );
    };

    /**
     * Updates the character rotations.
     *
     * @param selectedSlot Slot of the character.
     * @param rotation Slot of the rotation partner.
     */
    const updateRotations = (
        selectedSlot: MaybeRef<number>,
        rotation: MaybeRef<number>,
    ): void => {
        const slotValue = toValue(selectedSlot);
        const rotationValue = toValue(rotation);

        log.debug("Updating rotations...", {
            slot: slotValue,
            rotation: rotationValue,
        });

        const previousRotation = rotations.value[slotValue];

        rotations.value[slotValue] = rotationValue;

        if (
            rotations.value.filter((r: number) => r !== null).length > 4 &&
            rotations.value[rotationValue] === null
        ) {
            useToast({
                title: "Oops!",
                text: "There can be up to 4 characters in rotations.",
                icon: "warning",
            });

            nextTick(() => {
                rotations.value[slotValue] = null;
            });

            return;
        }

        rotations.value[previousRotation] = null;
        rotations.value[rotationValue] = slotValue;

        filter();
    };

    /**
     * Clears a slot.
     *
     * @param selectedSlot
     */
    const clearSlot = (selectedSlot?: MaybeRef<number>): void => {
        const slotValue = toValue(selectedSlot);

        log.debug("Clearing slot...", {
            slot: slotValue,
        });

        if (slotValue === undefined) {
            characters.value.fill(null);
            rotations.value.fill(null);
        } else {
            characters.value[slotValue] = null;
        }

        if (slotValue === 0) {
            characters.value[6] = null;
        }

        filter();
    };

    /**
     * Checks if a slot is a rotation slot.
     *
     * @param selectedSlot
     *
     * @returns Whether the slot is a rotation slot.
     */
    const isRotationSlot = (selectedSlot?: MaybeRef<number>): boolean => {
        return (
            rotations.value.includes(toValue(selectedSlot) || slot.value) &&
            characters.value[rotations.value[slot.value]] !== null
        );
    };

    /**
     * Checks if a character is a duplicate.
     *
     * @param character Character to be checked.
     * @param slot Slot of the character, if it's a selected character.
     *
     * @returns Whether the character is a duplicate.
     */
    const isDuplicate = (
        character: MaybeRef<Character>,
        slot?: MaybeRef<number>,
    ): boolean => {
        return characters.value.some(
            (c: Character, i: number) =>
                i !== 6 &&
                i !== toValue(slot) &&
                c?.variant_id === toValue(character).variant_id,
        );
    };

    /**
     * Watches the selected slot and prefetches the suggested character data on change.
     */
    onMounted(() => {
        watch(
            () => slot.value,
            () => {
                if (slot.value === null) {
                    router.prefetch(
                        route("teams.create"),
                        {
                            method: "get",
                            data: { ...sortedData(true) },
                            onPrefetching: () => {
                                log.debug(
                                    "Prefetching suggested character data...",
                                    sortedData(true),
                                );
                            },
                            onPrefetched: () => {
                                log.info(
                                    "Suggested character data prefetched.",
                                );
                            },
                            preserveScroll: true,
                            preserveState: true,
                        },
                        {
                            cacheFor: "30s",
                        },
                    );
                }
            },
            { immediate: true },
        );
    });

    return {
        characters,
        slot,
        nextSlot,
        rotations,
        boxUser,
        categories,
        categoriesPartial,
        categoryAffinity,
        order,
        search,
        loading,
        selectSlot,
        addCharacter,
        updateRotations,
        clearSlot,
        filter,
        isRotationSlot,
        isDuplicate,
    };
}
